import { type ReactElement } from 'react'
import { Grid, Typography, ButtonBase } from '@mui/material'
import { setActiveStep } from '@/store/setUpInheritorsV2Slice'
import { useAppDispatch } from '@/store'

export enum StepStatus {
  COMPLETED = 'completed',
  IN_PROGRESS = 'in_progress',
  TODO = 'todo',
}

const StepperDesktop = ({
  stepStatus,
  chainIdAddress,
}: {
  stepStatus: StepStatus[]
  chainIdAddress: string
}): ReactElement => {
  const dispatch = useAppDispatch()

  const steps = [
    {
      title: 'Add inheritors',
      content: 'Provide details of who can inherit your assets and what percentage each person can receive.',
    },
    {
      title: 'Set inheritance rules',
      content: 'Customize when and how your inheritors receive assets by setting specific rules.',
    },
    {
      title: 'Prepare inheritance',
      content: 'Prepare your inheritance with existing funds or deposit to get started.',
    },
  ]

  const circleSvg = (color = '#22C55E') => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <g clipPath="url(#clip0_2766_2186)">
          <path
            d="M8.00008 13.3337C10.9456 13.3337 13.3334 10.9458 13.3334 8.00033C13.3334 5.05481 10.9456 2.66699 8.00008 2.66699C5.05456 2.66699 2.66675 5.05481 2.66675 8.00033C2.66675 10.9458 5.05456 13.3337 8.00008 13.3337Z"
            fill={color}
          />
        </g>
        <defs>
          <clipPath id="clip0_2766_2186">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    )
  }

  const checkSvg = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <g clipPath="url(#clip0_2805_7722)">
          <path
            d="M6.0001 10.7799L3.2201 7.9999L2.27344 8.9399L6.0001 12.6666L14.0001 4.66656L13.0601 3.72656L6.0001 10.7799Z"
            fill="#5D5DFF"
          />
        </g>
        <defs>
          <clipPath id="clip0_2805_7722">
            <rect width="16" height="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    )
  }

  const handleStepClick = (index: number) => {
    if (stepStatus[index] === StepStatus.COMPLETED) {
      dispatch(setActiveStep({ chainIdAddress, activeStep: index }))
    }
  }

  return (
    <Grid container>
      {steps.map((step, index) => {
        const color =
          stepStatus[index] === StepStatus.COMPLETED
            ? '#5D5DFF'
            : stepStatus[index] === StepStatus.IN_PROGRESS
            ? '#22C55E'
            : '#7C8AB4'

        const boxShadow =
          stepStatus[index] === StepStatus.COMPLETED || stepStatus[index] === StepStatus.IN_PROGRESS ? '2px' : '1px'

        const colorContent = stepStatus[index] === StepStatus.IN_PROGRESS ? '#D2D7E5' : '#7C8AB4'

        return (
          <ButtonBase
            key={`step_${index + 1}`}
            sx={{ textAlign: 'left', width: '100%' }}
            onClick={() => {
              handleStepClick(index)
            }}
          >
            <Grid
              item
              container
              xs={12}
              sx={{
                boxShadow: `${boxShadow} 0px 0px 0px ${color} inset`,
                padding: '16px',
                color: 'var(--Ebony-Clay-500, #7C8AB4)',
              }}
            >
              <Grid item xs="auto" pt="2px">
                {stepStatus[index] === StepStatus.COMPLETED ? checkSvg() : circleSvg(color)}
              </Grid>
              <Grid item xs sx={{ marginLeft: '8px' }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    fontStyle: 'normal',
                    lineHeight: '160%',
                    marginBottom: '8px',
                    color,
                  }}
                  variant="body1"
                  component="h2"
                >
                  Step {index + 1}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    fontStyle: 'normal',
                    lineHeight: '160%',
                    color,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: '400',
                    fontStyle: 'normal',
                    lineHeight: '160%',
                    color: colorContent,
                  }}
                >
                  {step.content}
                </Typography>
              </Grid>
            </Grid>
          </ButtonBase>
        )
      })}
    </Grid>
  )
}

export default StepperDesktop
